// Service tier definitions and feature access control
export const SUBSCRIPTION_TIERS = {
  BASIC: 'basic',
  PROFESSIONAL: 'professional',
  ENTERPRISE: 'enterprise'
};

export const TIER_FEATURES = {
  [SUBSCRIPTION_TIERS.BASIC]: {
    name: 'Basic Guard',
    color: 'text-blue-400',
    bgColor: 'bg-blue-900/20',
    features: [
      'multi_engine_scanner', // Full file, URL, IP scanning
      'security_score',
      'basic_ai_protection',
      'threat_intelligence',
      'email_support'
    ],
    limits: {
      daily_scans: 50,
      file_size_mb: 100,
      support: 'email'
    }
  },
  
  [SUBSCRIPTION_TIERS.PROFESSIONAL]: {
    name: 'Professional Guard', 
    color: 'text-purple-400',
    bgColor: 'bg-purple-900/20',
    features: [
      'multi_engine_scanner',
      'security_score', 
      'email_protection', // Email virus protection & attachment scanner
      'anti_surveillance', // Anti-surveillance scanner (wiretap detection)
      'vpn_service', // VPN & Network Protection
      'advanced_ai_protection',
      'network_monitor',
      'real_time_scanning',
      'global_threat_intelligence',
      'priority_support'
    ],
    limits: {
      daily_scans: 500,
      file_size_mb: 500,
      support: 'priority_email'
    }
  },
  
  [SUBSCRIPTION_TIERS.ENTERPRISE]: {
    name: 'Enterprise Premium Guard',
    color: 'text-yellow-400', 
    bgColor: 'bg-yellow-900/20',
    features: [
      'multi_engine_scanner',
      'security_score',
      'email_protection',
      'advanced_email_dashboard', // Advanced email security dashboard
      'real_time_email_monitoring', // Real-time email monitoring & alerts
      'anti_surveillance',
      'vpn_service', // VPN & Network Protection
      'advanced_ai_protection',
      'network_monitor',
      'real_time_scanning',
      'global_threat_intelligence',
      'api_access',
      'custom_reports',
      'white_label',
      'dedicated_support',
      'sla_guarantee'
    ],
    limits: {
      daily_scans: 'unlimited',
      file_size_mb: 'unlimited', 
      support: '24/7_dedicated'
    }
  }
};

// Helper function to get user's subscription tier
export const getUserTier = (user) => {
  if (!user) return SUBSCRIPTION_TIERS.BASIC; // Default to Basic (no free tier)

  // Admin users get enterprise-level access
  if (user.role === 'admin' ||
      user.subscription_tier === 'admin' ||
      user.email === 'admin@cyberguardglobal.com' ||
      user.email === 'team@cyberguardglobal.com') {
    return SUBSCRIPTION_TIERS.ENTERPRISE;
  }

  // Demo user tier mapping for development
  const demoUsers = {
    'basic@customer.com': SUBSCRIPTION_TIERS.BASIC,
    'pro@customer.com': SUBSCRIPTION_TIERS.PROFESSIONAL,
    'professional@customer.com': SUBSCRIPTION_TIERS.PROFESSIONAL,
    'enterprise@customer.com': SUBSCRIPTION_TIERS.ENTERPRISE
  };

  // Check demo users first
  if (demoUsers[user.email]) {
    return demoUsers[user.email];
  }

  // Check user's subscription data
  if (user.subscription_tier) {
    return user.subscription_tier.toLowerCase();
  }

  // Check plan_id if subscription_tier not available
  if (user.plan_id) {
    return user.plan_id.toLowerCase();
  }

  // Default to Basic if no subscription info (no free tier)
  return SUBSCRIPTION_TIERS.BASIC;
};

// Helper function to check if user has access to a feature
export const hasFeatureAccess = (user, feature) => {
  const tier = getUserTier(user);
  const tierData = TIER_FEATURES[tier];
  
  if (!tierData) {
    return false;
  }
  
  return tierData.features.includes(feature);
};

// Helper function to get tier display info
export const getTierInfo = (user) => {
  const tier = getUserTier(user);
  return TIER_FEATURES[tier] || TIER_FEATURES[SUBSCRIPTION_TIERS.FREE];
};

// Helper function to check scanning limits
export const checkScanLimit = (user, currentScans = 0) => {
  const tierInfo = getTierInfo(user);
  const dailyLimit = tierInfo.limits.daily_scans;
  
  if (dailyLimit === 'unlimited') {
    return { allowed: true, remaining: 'unlimited' };
  }
  
  const remaining = Math.max(0, dailyLimit - currentScans);
  return { 
    allowed: remaining > 0, 
    remaining,
    limit: dailyLimit 
  };
};

// Features that require specific subscription tiers
export const FEATURE_GATES = {
  // Basic features (available to Basic and above)
  MULTI_ENGINE_SCANNER: [SUBSCRIPTION_TIERS.BASIC, SUBSCRIPTION_TIERS.PROFESSIONAL, SUBSCRIPTION_TIERS.ENTERPRISE],
  THREAT_INTELLIGENCE: [SUBSCRIPTION_TIERS.BASIC, SUBSCRIPTION_TIERS.PROFESSIONAL, SUBSCRIPTION_TIERS.ENTERPRISE],
  
  // Professional features (Professional and above)
  EMAIL_PROTECTION: [SUBSCRIPTION_TIERS.PROFESSIONAL, SUBSCRIPTION_TIERS.ENTERPRISE],
  ANTI_SURVEILLANCE: [SUBSCRIPTION_TIERS.PROFESSIONAL, SUBSCRIPTION_TIERS.ENTERPRISE],
  NETWORK_MONITOR: [SUBSCRIPTION_TIERS.PROFESSIONAL, SUBSCRIPTION_TIERS.ENTERPRISE],
  
  // Enterprise features (Enterprise only)
  ADVANCED_EMAIL_DASHBOARD: [SUBSCRIPTION_TIERS.ENTERPRISE],
  API_ACCESS: [SUBSCRIPTION_TIERS.ENTERPRISE],
  CUSTOM_REPORTS: [SUBSCRIPTION_TIERS.ENTERPRISE],
  WHITE_LABEL: [SUBSCRIPTION_TIERS.ENTERPRISE]
};

export const getUpgradeMessage = (currentTier, requiredFeature) => {
  const messages = {
    [SUBSCRIPTION_TIERS.FREE]: "Upgrade to Basic Guard to unlock multi-engine scanning and advanced threat protection.",
    [SUBSCRIPTION_TIERS.BASIC]: "Upgrade to Professional Guard to access email protection, anti-surveillance scanning, and network monitoring.",
    [SUBSCRIPTION_TIERS.PROFESSIONAL]: "Upgrade to Enterprise Premium Guard for advanced dashboards, API access, and 24/7 dedicated support."
  };
  
  return messages[currentTier] || "Upgrade your plan to access this premium feature.";
};